/* istanbul ignore next */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  NOTIFICATION_LEVEL_ERROR,
  NOTIFICATION_LEVEL_SUCCESS,
} from '@avtechsoftware/ra-javascript-utilities/src/types/NotificationLevels';
import ModalComponent from '../../../../../layout/Modals/ModalComponent/ModalComponent';
import { connectModalContainerContextToComponent } from '../../../../../../contexts/ModalContext/ModalContext';
import NotificationGroupPropType from '../../../../../../utility/PropTypes/notificationGroupPropType';
import NotificationGroupUtility, {
  transportTypeDescriptionMap,
} from '../../../../../../utility/NotificationGroupUtility';
import { selectUserList } from '../../../../../../redux/slices/userListSlice/userListSlice';
import { getFullUserName } from '../../../../../../utility/getFullUserName';
import { pushSessionMessage } from '../../../../../../redux/slices/toastNotificationSlice/toastNotificationSlice';
import RaaAuthenticatedRemoteService from '../../../../../../utility/RaaAuthenticatedRemoteService';
import envConfig from '../../../../../../envConfig';

function NotificationGroupDetailsModal({ setModalJSX, notificationGroup }) {
  const dispatch = useDispatch();
  const users = useSelector(selectUserList);
  const [show, setShow] = useState(true);
  const [sendingRecipientId, setSendingRecipientId] = useState(null);
  const [sentRecipientIds, setSentRecipientIds] = useState(new Set());

  const getRecipientDetailsArray = (recipients) => recipients.map((recipient) => {
    const { transportId, userId, context } = recipient;
    const recipientDetail = userId !== null
      ? getFullUserName(userId, users)
      : NotificationGroupUtility.getContextStringByTransportTypeId(
        transportId,
        context,
      );

    return `${transportTypeDescriptionMap[transportId]} ${recipientDetail}`;
  });

  const recipientDetailsStringArray = getRecipientDetailsArray(notificationGroup.recipients);

  const sendTestNotification = async (recipient) => {
    const response = await RaaAuthenticatedRemoteService.globalInstance().post(`${envConfig.apiUrl}/api/v1/notification-group/recipient/test/${recipient.id}`);
    if (!response.recipientId) {
      throw new Error();
    }

    return true;
  };

  const showToast = (type, message) => {
    dispatch(pushSessionMessage({
      level: type,
      title: type === NOTIFICATION_LEVEL_ERROR ? 'Test Notification Action Error' : 'Test Notification Action Success',
      body: message,
    }));
  };

  const handleSendTest = async (recipient, index) => {
    try {
      setSendingRecipientId(index);

      await sendTestNotification(recipient);

      setSentRecipientIds((prev) => new Set(prev).add(index));

      showToast(NOTIFICATION_LEVEL_SUCCESS, 'Test message sent successfully');
    } catch (error) {
      showToast(NOTIFICATION_LEVEL_ERROR, 'Failed to send test message');
    } finally {
      setSendingRecipientId(null);
    }
  };

  const closeModalHandler = () => {
    setShow(false);
    setModalJSX(<></>);
  };

  const getButtonLabel = (index) => {
    if (sendingRecipientId === index) return 'Sending...';
    if (sentRecipientIds.has(index)) return 'Sent';
    return 'Send Test Message';
  };

  return (
    <ModalComponent
      showConfirmCallToAction={false}
      show={show}
      close={closeModalHandler}
    >
      <Modal.Header>
        <Modal.Title>
          {notificationGroup.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="p-2">
          <h6>
            Notifies
            {' '}
            {notificationGroup.recipients.length}
            {' '}
            {notificationGroup.recipients.length === 1 ? 'recipient' : 'recipients'}
          </h6>

          {
              notificationGroup.recipients.length > 0
                ? (
                  <div className="mt-2">
                    <ul
                      className="list-group overflow-auto"
                      style={{ maxHeight: '250px' }}
                    >
                      {recipientDetailsStringArray.map((string, index) => (
                        <li
                          key={index}
                          className={`list-group-item d-flex justify-content-between align-items-center ${
                            index % 2 === 0 ? 'bg-light' : ''
                          }`}
                        >
                          <span>{string}</span>

                          <button
                            type="button"
                            className="btn btn-sm btn-primary text-nowrap"
                            disabled={
                                        sentRecipientIds.has(index)
                                || (sendingRecipientId !== null && sendingRecipientId !== index)
                                    }
                            onClick={
                            () => handleSendTest(notificationGroup.recipients[index], index)
}
                          >
                            {getButtonLabel(index)}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )
                : <span>No recipients</span>
            }
        </div>
      </Modal.Body>
    </ModalComponent>
  );
}

NotificationGroupDetailsModal.propTypes = {
  setModalJSX: PropTypes.func.isRequired,
  notificationGroup: PropTypes.shape(NotificationGroupPropType).isRequired,
};
export default connectModalContainerContextToComponent(NotificationGroupDetailsModal);
